import { FC, useMemo, useState } from 'react';
import { fetchOrders, OrderResponse, OrderServiceTechnician, updateOrder } from '../../../api/orders';
import { Bay } from '../../../api/bays';
import { UserProfile } from '../../../api/auth';
import SchedulerItemBlock from './SchedulerItemBlock';
import AssignmentEditModal from './AssignmentEditModal';
import { getUtilizationColor, getUtilizationBgColor } from '../../../utils/utilization';

interface BayViewProps {
    orders: OrderResponse[];
    bays: Bay[];
    selectedDate: Date;
    technicians: UserProfile[];
    startHour: number;
    endHour: number;
    onRefresh: () => void;
}

const BayView: FC<BayViewProps> = ({ orders, bays, selectedDate, technicians, startHour, endHour, onRefresh }) => {
    const TOTAL_HOURS = endHour - startHour;
    const HOUR_HEIGHT = 70; // px per hour
    const WORKING_HOURS = TOTAL_HOURS; // Assume working hours match opening hours for now

    const TIME_SLOTS = useMemo(() => {
        const slots = [];
        for (let i = startHour; i < endHour; i++) {
            const h = i % 12 || 12;
            const ampm = i < 12 || i === 24 ? 'AM' : 'PM';
            const displayH = i === 0 || i === 24 ? 12 : h;
            slots.push(`${displayH} ${ampm}`);
        }
        return slots;
    }, [startHour, endHour]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedAssignment, setSelectedAssignment] = useState<{ order: OrderResponse; assignment: OrderServiceTechnician } | null>(null);

    // Filter orders that have assignments to these bays and overlap with the selected date
    const activeAssignments = useMemo(() => {
        const viewStart = new Date(selectedDate);
        viewStart.setHours(0, 0, 0, 0);
        const viewEnd = new Date(selectedDate);
        viewEnd.setHours(23, 59, 59, 999);

        return orders.flatMap(order =>
            (order.serviceTechnicians || []).map(assignment => ({
                order,
                assignment
            }))
        ).filter(item => {
            if (!item.assignment.bayId || !item.assignment.startTime || !item.assignment.endTime) return false;
            const start = new Date(item.assignment.startTime);
            const end = new Date(item.assignment.endTime);
            // Overlap check: Assignment strictly overlaps with the selected date
            return start <= viewEnd && end >= viewStart;
        });
    }, [orders, selectedDate]);

    // Filter orders for sidebar (Strict Operational Logic)
    const sidebarOrders = useMemo(() => {
        const rangeStart = new Date(selectedDate);
        rangeStart.setHours(0, 0, 0, 0);
        const rangeEnd = new Date(selectedDate);
        rangeEnd.setHours(23, 59, 59, 999);

        return orders.filter(order => {
            // 1. Check if assigned on date (Overlap check)
            const hasAssignmentsOnDate = (order.serviceTechnicians || []).some(st => {
                if (!st.startTime || !st.endTime) return false;
                const start = new Date(st.startTime);
                const end = new Date(st.endTime);
                return start <= rangeEnd && end >= rangeStart;
            });

            // 2. Check if created on date
            const createdOnDate = new Date(order.createdAt).toDateString() === selectedDate.toDateString();

            // STRICT FILTER: Only show relevant orders
            return hasAssignmentsOnDate || createdOnDate;
        });
    }, [orders, selectedDate]);

    const getTimePosition = (assignment: OrderServiceTechnician) => {
        const startTime = assignment.startTime;
        const endTime = assignment.completed && assignment.completedAt ? assignment.completedAt : assignment.endTime;

        if (!startTime || !endTime) return { left: '0%', width: '0%', isClamped: false };

        const start = new Date(startTime);
        const end = new Date(endTime);

        const viewStart = new Date(selectedDate);
        viewStart.setHours(startHour, 0, 0, 0);
        const viewEnd = new Date(selectedDate);
        viewEnd.setHours(endHour, 0, 0, 0);

        let assignmentStart = start.getTime();
        let assignmentEnd = end.getTime();

        const vStart = viewStart.getTime();
        const vEnd = viewEnd.getTime();

        // Check if completely out of view
        if (assignmentEnd <= vStart || assignmentStart >= vEnd) {
            return { left: '0%', width: '0%', isHidden: true };
        }

        // Clamp to view range
        const renderStart = Math.max(assignmentStart, vStart);
        const renderEnd = Math.min(assignmentEnd, vEnd);

        const left = ((renderStart - vStart) / (vEnd - vStart)) * 100;
        const width = ((renderEnd - renderStart) / (vEnd - vStart)) * 100;

        return {
            left: `${Math.max(0, left)}%`,
            width: `${Math.max(0.5, width)}%`, // Minimum width to ensure visibility 
            isClampedStart: assignmentStart < vStart,
            isClampedEnd: assignmentEnd > vEnd
        };
    };

    const getUtilization = (bayId: string) => {
        const assignments = activeAssignments.filter(a =>
            a.assignment.bayId === bayId &&
            a.assignment.startTime &&
            new Date(a.assignment.startTime).toDateString() === selectedDate.toDateString()
        );

        const totalDurationMs = assignments.reduce((acc, curr) => {
            if (curr.assignment.startTime && curr.assignment.endTime) {
                return acc + (new Date(curr.assignment.endTime).getTime() - new Date(curr.assignment.startTime).getTime());
            }
            return acc;
        }, 0);

        const totalHours = totalDurationMs / (1000 * 60 * 60);
        return Math.min(100, Math.round((totalHours / WORKING_HOURS) * 100));
    };

    const getTechnicianUtilization = (techId: string) => {
        const assignments = activeAssignments.filter(a =>
            a.assignment.technicianId === techId &&
            a.assignment.startTime &&
            new Date(a.assignment.startTime).toDateString() === selectedDate.toDateString()
        );

        const totalDurationMs = assignments.reduce((acc, curr) => {
            if (curr.assignment.startTime && curr.assignment.endTime) {
                return acc + (new Date(curr.assignment.endTime).getTime() - new Date(curr.assignment.startTime).getTime());
            }
            return acc;
        }, 0);

        const totalHours = totalDurationMs / (1000 * 60 * 60);
        return Math.min(100, Math.round((totalHours / WORKING_HOURS) * 100));
    };

    const getOverlappingAssignments = (bayId: string) => {
        const assignments = activeAssignments.filter(a =>
            a.assignment.bayId === bayId &&
            a.assignment.startTime &&
            new Date(a.assignment.startTime).toDateString() === selectedDate.toDateString()
        );

        const overlappingIds = new Set<string>();

        for (let i = 0; i < assignments.length; i++) {
            for (let j = i + 1; j < assignments.length; j++) {
                const a1 = assignments[i];
                const a2 = assignments[j];
                const start1 = new Date(a1.assignment.startTime!).getTime();
                const end1 = new Date(a1.assignment.endTime!).getTime();
                const start2 = new Date(a2.assignment.startTime!).getTime();
                const end2 = new Date(a2.assignment.endTime!).getTime();

                if (start1 < end2 && start2 < end1) {
                    if (a1.order.id !== a2.order.id) {
                        overlappingIds.add(a1.assignment.id);
                        overlappingIds.add(a2.assignment.id);
                    }
                }
            }
        }
        return overlappingIds;
    };

    const handleAssignmentClick = (order: OrderResponse, assignment: OrderServiceTechnician) => {
        setSelectedAssignment({ order, assignment });
        setEditModalOpen(true);
    };

    const handleSaveAssignment = async (assignmentId: string, data: any) => {
        const { autoShift, ...updates } = data;
        const target = activeAssignments.find(a => a.assignment.id === assignmentId);
        if (!target) return;

        const originalOrder = target.order;
        const bayId = target.assignment.bayId;
        const oldEndTime = target.assignment.endTime ? new Date(target.assignment.endTime).getTime() : 0;
        const newEndTime = updates.endTime ? new Date(updates.endTime).getTime() : 0;
        const delayMs = newEndTime - oldEndTime;

        try {
            const currentOrderAssignments = (originalOrder.serviceTechnicians || []).map(st => {
                if (st.id === assignmentId) {
                    return {
                        orderItemId: st.orderItemId,
                        technicianId: st.technicianId,
                        bayId: updates.bayId,
                        startTime: updates.startTime,
                        endTime: updates.endTime,
                        notes: updates.notes,
                        completed: updates.completed
                    };
                }
                return {
                    orderItemId: st.orderItemId,
                    technicianId: st.technicianId,
                    bayId: st.bayId,
                    startTime: st.startTime,
                    endTime: st.endTime,
                    notes: st.notes,
                    completed: st.completed
                };
            });

            if (autoShift && delayMs !== 0 && bayId) {
                const subsequent = activeAssignments.filter(a =>
                    a.assignment.bayId === bayId &&
                    a.assignment.id !== assignmentId &&
                    a.assignment.startTime &&
                    new Date(a.assignment.startTime).getTime() >= oldEndTime &&
                    new Date(a.assignment.startTime).toDateString() === selectedDate.toDateString()
                ).sort((a, b) => new Date(a.assignment.startTime!).getTime() - new Date(b.assignment.startTime!).getTime());

                const ordersToUpdate = new Map<string, any[]>();

                subsequent.forEach(a => {
                    const orderId = a.order.id;
                    if (!ordersToUpdate.has(orderId)) {
                        ordersToUpdate.set(orderId, (a.order.serviceTechnicians || []).map(st => ({
                            id: st.id,
                            orderItemId: st.orderItemId,
                            technicianId: st.technicianId,
                            bayId: st.bayId,
                            startTime: st.startTime,
                            endTime: st.endTime,
                            notes: st.notes,
                            completed: st.completed
                        })));
                    }

                    const orderAssignments = ordersToUpdate.get(orderId)!;
                    const entry = orderAssignments.find(ea => ea.id === a.assignment.id);
                    if (entry && entry.startTime && entry.endTime) {
                        const s = new Date(entry.startTime).getTime() + delayMs;
                        const e = new Date(entry.endTime).getTime() + delayMs;
                        entry.startTime = new Date(s).toISOString();
                        entry.endTime = new Date(e).toISOString();
                    }
                });

                if (ordersToUpdate.has(originalOrder.id)) {
                    const shiftedSelf = ordersToUpdate.get(originalOrder.id)!;
                    const targetIdx = shiftedSelf.findIndex(s => s.id === assignmentId);
                    if (targetIdx !== -1) {
                        shiftedSelf[targetIdx] = {
                            ...shiftedSelf[targetIdx],
                            startTime: updates.startTime,
                            endTime: updates.endTime,
                            notes: updates.notes,
                            completed: updates.completed
                        };
                    }
                    await updateOrder(originalOrder.id, { serviceAssignments: shiftedSelf });
                    ordersToUpdate.delete(originalOrder.id);
                } else {
                    await updateOrder(originalOrder.id, { serviceAssignments: currentOrderAssignments });
                }

                for (const [oid, assignments] of Array.from(ordersToUpdate.entries())) {
                    await updateOrder(oid, { serviceAssignments: assignments });
                }
            } else {
                await updateOrder(originalOrder.id, { serviceAssignments: currentOrderAssignments });
            }

            onRefresh();
        } catch (error) {
            console.error('Failed to save assignment', error);
            alert('Failed to save assignment changes.');
        }
    };

    return (
        <div className="flex bg-[#f8faff] min-h-[600px]">
            {/* Sidebar: Service Orders */}
            <div className="w-[240px] border-r border-slate-200 bg-white shrink-0 flex flex-col h-full">
                <div className="p-4 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Service Orders</h3>
                    <div className="relative group/legend">
                        <svg className="w-4 h-4 text-slate-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <div className="absolute right-0 top-6 w-48 p-2 bg-white rounded-lg shadow-xl border border-slate-100 z-50 hidden group-hover/legend:block">
                            <div className="text-[10px] font-semibold text-slate-500 mb-2 uppercase tracking-wider">Status Legend</div>
                            <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-primary" />
                                    <span className="text-xs text-slate-600">In Progress</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-slate-300" />
                                    <span className="text-xs text-slate-600">Other</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto min-h-0 divide-y divide-slate-100">
                    {sidebarOrders.map((order) => {
                        const statusLabel = order.status === 'in_progress' ? 'In Progress' : order.status.replace(/_/g, ' ');
                        return (
                            <div key={order.id} className="p-4 hover:bg-slate-50 transition-colors cursor-pointer group">
                                <div className="flex flex-col gap-1.5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-bold text-slate-400">SO No: {order.orderNumber}</span>
                                        <span
                                            title={`Status: ${statusLabel}`}
                                            className={`h-2 w-2 rounded-full ${order.status === 'in_progress' ? 'bg-primary' : 'bg-slate-300'}`}
                                        />
                                    </div>
                                    <p className="text-xs font-bold text-slate-800 group-hover:text-primary transition-colors">
                                        Reg No: {order.customerVehicle?.vehicleRegistrationNumber || 'N/A'}
                                    </p>
                                    <div className="text-[11px] text-slate-500 leading-tight">
                                        <p>Make: {order.customerVehicle?.vehicleBrand?.name || 'N/A'}</p>
                                        <p>Model: {order.customerVehicle?.vehicleModel?.name || 'N/A'}</p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Main Grid Area */}
            <div className="flex-1 overflow-x-auto">
                <div className="min-w-[1000px]">
                    {/* Header: Time Slots */}
                    <div className="flex border-b border-slate-200 bg-white sticky top-0 z-10">
                        <div className="w-40 shrink-0 p-4 border-r border-slate-200" /> {/* Spacer for Bay column */}
                        <div className="flex flex-1">
                            {TIME_SLOTS.map((time) => (
                                <div key={time} className="flex-1 p-4 text-center border-l border-slate-100 font-bold text-[11px] text-slate-400 uppercase tracking-widest bg-slate-50/30">
                                    {time}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Body: Bay Rows */}
                    <div className="divide-y divide-slate-200">
                        {bays.length === 0 ? (
                            <div className="p-12 text-center text-slate-400 italic">No active bays found.</div>
                        ) : (
                            bays.map((bay) => {
                                const utilization = getUtilization(bay.id);
                                const bayAssignments = activeAssignments.filter(a =>
                                    a.assignment.bayId === bay.id &&
                                    a.assignment.startTime &&
                                    new Date(a.assignment.startTime).toDateString() === selectedDate.toDateString()
                                );
                                const overlappingIds = getOverlappingAssignments(bay.id);

                                return (
                                    <div key={bay.id} className="flex group">
                                        {/* Bay Label */}
                                        <div className="w-40 shrink-0 p-2 border-r border-slate-200 flex flex-col justify-center bg-white group-hover:bg-slate-50 transition-colors">
                                            <p className="text-[10px] font-bold text-slate-800">{bay.name}</p>
                                            <div className="mt-0.5 flex items-center justify-between">
                                                <span className={`text-[9px] font-bold ${getUtilizationColor(utilization)}`}>{utilization}%</span>
                                            </div>
                                            <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                                                <div className={`h-full rounded-full ${getUtilizationBgColor(utilization)}`} style={{ width: `${utilization}%` }} />
                                            </div>
                                        </div>

                                        {/* Bay Time Grid */}
                                        <div className="flex flex-1 relative min-h-[140px] bg-slate-50/20 group-hover:bg-transparent transition-colors">
                                            {/* Grid background markers */}
                                            {TIME_SLOTS.map((_, idx) => (
                                                <div key={idx} className="flex-1 border-l border-slate-100/50" />
                                            ))}

                                            {/* Service Blocks relative positioning */}
                                            <div className="absolute inset-0 w-full h-full">
                                                {bayAssignments.map((item, idx) => {
                                                    const pos = getTimePosition(item.assignment);
                                                    if ((pos as any).isHidden) return null;

                                                    const isOverlapping = overlappingIds.has(item.assignment.id);

                                                    // Dynamic z-index and slight offset for overlaps
                                                    const zIndex = isOverlapping ? 20 + idx : 10;
                                                    const topOffset = isOverlapping ? (idx % 2 === 0 ? '8px' : '32px') : '8px';
                                                    const bottomOffset = isOverlapping ? (idx % 2 === 0 ? '32px' : '8px') : '8px';

                                                    return (
                                                        <div
                                                            key={`${item.order.id}-${idx}`}
                                                            className={`absolute transition-all hover:z-50 ${isOverlapping ? 'ring-2 ring-red-500 ring-offset-1' : ''}`}
                                                            style={{
                                                                left: pos.left,
                                                                width: pos.width,
                                                                top: topOffset,
                                                                bottom: bottomOffset,
                                                                zIndex
                                                            }}
                                                            onClick={() => handleAssignmentClick(item.order, item.assignment)}
                                                        >
                                                            <div className="h-full w-full overflow-hidden relative">
                                                                {(pos as any).isClampedStart && (
                                                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary/30 z-10" title="Starts before opening" />
                                                                )}
                                                                {(pos as any).isClampedEnd && (
                                                                    <div className="absolute right-0 top-0 bottom-0 w-1 bg-primary/30 z-10" title="Ends after closing" />
                                                                )}
                                                                <SchedulerItemBlock order={item.order} assignment={item.assignment} view="bay" />
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

            {/* Right Sidebar: Technicians Status (Matching Screenshot) */}
            <div className="w-[180px] border-l border-slate-200 bg-white shrink-0 hidden lg:block">
                <div className="p-4 border-b border-slate-200 bg-slate-50/50">
                    <h3 className="font-bold text-slate-800 text-sm uppercase tracking-wide">Technicians</h3>
                </div>
                <div className="p-3 space-y-4">
                    {technicians.slice(0, 8).map((tech) => {
                        const utilization = getTechnicianUtilization(tech.id);
                        return (
                            <div key={tech.id} className="flex items-center gap-3">
                                <div className="relative">
                                    <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-primary font-bold shadow-sm overflow-hidden border-2 border-white ring-1 ring-slate-200">
                                        {tech.firstName?.[0] || tech.username?.[0] || 'T'}
                                    </div>
                                    <span className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${utilization > 0 ? 'bg-emerald-500' : 'bg-slate-300'} shadow-sm`} />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-xs font-bold text-slate-800 truncate">{tech.firstName || tech.username}</p>
                                    <div className="flex items-center gap-1">
                                        <span className={`text-[10px] font-bold ${getUtilizationColor(utilization)}`}>{utilization}%</span>
                                        <svg className={`w-2.5 h-2.5 ${utilization > 80 ? 'text-danger' : 'text-emerald-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            {utilization > 50 ? (
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                            ) : (
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                            )}
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {selectedAssignment && (
                <AssignmentEditModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    assignment={selectedAssignment.assignment}
                    order={selectedAssignment.order}
                    bays={bays}
                    onSave={handleSaveAssignment}
                />
            )}
        </div>
    );
};

export default BayView;
